import Template from "../../components/Template";
import React, { useState } from "react";
import api from "../../services/api";
import AlertDialog from "../../components/AlertDialog";
import * as XLSX from "xlsx-js-style";
import { formatDateWithoutHour } from "../../utils/date";
import { formatCurrencyFromDb } from "../../utils/masks";

const RelatorioContabilidade = () => {
    const [records, setRecords] = useState([]);
    const [empresa, setEmpresa] = useState("");
    const [cnpj, setCnpj] = useState("");
    const [filters, setFilters] = useState({ dataInicio: "", dataFim: "" });
    const [isLoading, setIsLoading] = useState(false);
    const [hasSearched, setHasSearched] = useState(false);
    const [message, setMessage] = useState("");
    const [showSuccessDialog, setShowSuccessDialog] = useState(false);

    const fetchRelatorio = async () => {
        setIsLoading(true);
        setHasSearched(true);
        try {
            const params = new URLSearchParams();
            if (filters.dataInicio) params.append("dataInicio", filters.dataInicio);
            if (filters.dataFim) params.append("dataFim", filters.dataFim);
            const response = await api.get(`/relatorios/contabilidade?${params.toString()}`);
            setRecords(response.data.records || []);
            setEmpresa(response.data.empresa || "");
            setCnpj(response.data.cnpj || "");
        } catch (error) {
            console.error("Erro ao buscar relatório de contabilidade:", error);
            setMessage(error.response?.data?.error || "Erro ao buscar dados do relatório.");
            setShowSuccessDialog(true);
            setRecords([]);
        } finally {
            setIsLoading(false);
        }
    };

    const applyFilters = () => fetchRelatorio();
    const clearFilters = () => {
        setFilters({ dataInicio: "", dataFim: "" });
        setRecords([]);
        setHasSearched(false);
    };

    /**
     * Exporta o XLSX exatamente no modelo do escritório de contabilidade:
     *
     *  Linha 1: "GRUPO PERICIA - ECOSSISTEMA EMPRESARIAL" (mesclada cols A..E) | "Preenchimento Grupo Pericia" (cols F..G)
     *  Linha 2: "EMPRESA: <nome>"  (mesclada A..E)
     *  Linha 3: "CNPJ: <cnpj>"     (mesclada A..E)
     *  Linha 4: cabeçalhos:
     *     A: DATA DO PAGAMENTO
     *     B: NÚMERO DA NOTA OU BOLETO
     *     C: TIPO DE DOCUMENTO (RAZÃO SOCIAL)
     *     D: DESCRIÇÃO (TIPO DE DESPESA)
     *     E: VALOR
     *     F: DÉBITO   (vazio)
     *     G: CRÉDITO  (vazio)
     *  Linhas 5..n: dados (colunas F e G permanecem vazias)
     */
    const exportarExcel = () => {
        if (!records || records.length === 0) {
            setMessage("Não há dados para exportar.");
            setShowSuccessDialog(true);
            return;
        }

        const borderThin = { style: "thin", color: { rgb: "B0B0B0" } };
        const borders = { top: borderThin, bottom: borderThin, left: borderThin, right: borderThin };

        const tituloEsquerdoStyle = {
            font: { bold: true, sz: 12, color: { rgb: "000000" } },
            fill: { fgColor: { rgb: "D9E7F5" } },
            alignment: { horizontal: "center", vertical: "center" },
            border: borders,
        };
        const tituloDireitoStyle = {
            font: { sz: 10, color: { rgb: "000000" } },
            fill: { fgColor: { rgb: "FCE4D6" } },
            alignment: { horizontal: "center", vertical: "center", wrapText: true },
            border: borders,
        };
        const empresaLineStyle = {
            font: { bold: true, sz: 11, color: { rgb: "000000" } },
            fill: { fgColor: { rgb: "DCE6F1" } },
            alignment: { horizontal: "left", vertical: "center" },
            border: borders,
        };
        const empresaLineRightStyle = {
            fill: { fgColor: { rgb: "FCE4D6" } },
            border: borders,
        };
        const headerStyle = {
            font: { bold: true, sz: 10, color: { rgb: "000000" } },
            fill: { fgColor: { rgb: "DCE6F1" } },
            alignment: { horizontal: "center", vertical: "center", wrapText: true },
            border: borders,
        };
        const headerDebitoCreditoStyle = {
            font: { bold: true, sz: 10, color: { rgb: "000000" } },
            fill: { fgColor: { rgb: "FCE4D6" } },
            alignment: { horizontal: "center", vertical: "center", wrapText: true },
            border: borders,
        };
        const cellStyle = {
            font: { sz: 10, color: { rgb: "000000" } },
            fill: { fgColor: { rgb: "EAF1F8" } },
            alignment: { vertical: "center", horizontal: "left" },
            border: borders,
        };
        const cellCurrencyStyle = {
            font: { sz: 10, color: { rgb: "000000" } },
            fill: { fgColor: { rgb: "EAF1F8" } },
            alignment: { vertical: "center", horizontal: "right" },
            border: borders,
            numFmt: 'R$ #,##0.00',
        };
        const cellDebitoCreditoStyle = {
            fill: { fgColor: { rgb: "FFFFFF" } },
            border: borders,
        };

        const colCount = 7; // A..G

        // Monta dados
        const wsData = [];
        const merges = [];

        // Linha 0: Título à esquerda (A..E) + título à direita (F..G)
        const linhaTitulo = Array(colCount).fill("");
        linhaTitulo[0] = "GRUPO PERICIA - ECOSSISTEMA EMPRESARIAL";
        linhaTitulo[5] = "Preenchimento Grupo Pericia";
        wsData.push(linhaTitulo);
        merges.push({ s: { r: 0, c: 0 }, e: { r: 0, c: 4 } });
        merges.push({ s: { r: 0, c: 5 }, e: { r: 0, c: 6 } });

        // Linha 1: EMPRESA
        const linhaEmpresa = Array(colCount).fill("");
        linhaEmpresa[0] = `EMPRESA: ${empresa || ""}`;
        wsData.push(linhaEmpresa);
        merges.push({ s: { r: 1, c: 0 }, e: { r: 1, c: 4 } });
        merges.push({ s: { r: 1, c: 5 }, e: { r: 1, c: 6 } });

        // Linha 2: CNPJ
        const linhaCnpj = Array(colCount).fill("");
        linhaCnpj[0] = `CNPJ: ${cnpj || ""}`;
        wsData.push(linhaCnpj);
        merges.push({ s: { r: 2, c: 0 }, e: { r: 2, c: 4 } });
        merges.push({ s: { r: 2, c: 5 }, e: { r: 2, c: 6 } });

        // Linha 3: Cabeçalho
        const headerRowIndex = wsData.length;
        wsData.push([
            "DATA DO PAGAMENTO",
            "NÚMERO DA NOTA OU BOLETO",
            "TIPO DE DOCUMENTO (RAZÃO SOCIAL)",
            "DESCRIÇÃO (TIPO DE DESPESA)",
            "VALOR",
            "DÉBITO",
            "CRÉDITO",
        ]);

        // Linhas de dados
        const dataStartRow = wsData.length;
        records.forEach((r) => {
            wsData.push([
                r.dataEmissao ? formatDateWithoutHour(r.dataEmissao) : "",
                r.numero || "",
                (r.razaoSocial || "").toUpperCase(),
                r.tipoDespesa || "",
                Number(r.valor) || 0,
                "",
                "",
            ]);
        });
        const dataEndRow = wsData.length - 1;

        // Cria worksheet
        const worksheet = XLSX.utils.aoa_to_sheet(wsData);
        worksheet["!merges"] = merges;

        // Estilo: linha de título (linha 0)
        for (let c = 0; c <= 4; c++) {
            const ref = XLSX.utils.encode_cell({ r: 0, c });
            if (!worksheet[ref]) worksheet[ref] = { v: "", t: "s" };
            worksheet[ref].s = tituloEsquerdoStyle;
        }
        for (let c = 5; c <= 6; c++) {
            const ref = XLSX.utils.encode_cell({ r: 0, c });
            if (!worksheet[ref]) worksheet[ref] = { v: "", t: "s" };
            worksheet[ref].s = tituloDireitoStyle;
        }

        // Estilo: EMPRESA e CNPJ (linhas 1 e 2)
        for (let r = 1; r <= 2; r++) {
            for (let c = 0; c <= 4; c++) {
                const ref = XLSX.utils.encode_cell({ r, c });
                if (!worksheet[ref]) worksheet[ref] = { v: "", t: "s" };
                worksheet[ref].s = empresaLineStyle;
            }
            for (let c = 5; c <= 6; c++) {
                const ref = XLSX.utils.encode_cell({ r, c });
                if (!worksheet[ref]) worksheet[ref] = { v: "", t: "s" };
                worksheet[ref].s = empresaLineRightStyle;
            }
        }

        // Estilo: cabeçalho da tabela (linha headerRowIndex)
        for (let c = 0; c < colCount; c++) {
            const ref = XLSX.utils.encode_cell({ r: headerRowIndex, c });
            if (!worksheet[ref]) worksheet[ref] = { v: "", t: "s" };
            worksheet[ref].s = (c >= 5) ? headerDebitoCreditoStyle : headerStyle;
        }

        // Estilo: dados
        for (let r = dataStartRow; r <= dataEndRow; r++) {
            for (let c = 0; c < colCount; c++) {
                const ref = XLSX.utils.encode_cell({ r, c });
                if (!worksheet[ref]) worksheet[ref] = { v: "", t: "s" };
                if (c === 4) {
                    worksheet[ref].s = cellCurrencyStyle;
                    if (worksheet[ref].v !== "") worksheet[ref].t = "n";
                } else if (c >= 5) {
                    worksheet[ref].s = cellDebitoCreditoStyle;
                } else {
                    worksheet[ref].s = cellStyle;
                }
            }
        }

        // Larguras
        worksheet["!cols"] = [
            { wch: 20 }, // DATA DO PAGAMENTO
            { wch: 28 }, // NÚMERO
            { wch: 40 }, // RAZÃO SOCIAL
            { wch: 28 }, // DESCRIÇÃO
            { wch: 16 }, // VALOR
            { wch: 14 }, // DÉBITO
            { wch: 14 }, // CRÉDITO
        ];

        // Alturas (linha do título maior)
        worksheet["!rows"] = [{ hpt: 28 }, { hpt: 20 }, { hpt: 20 }, { hpt: 32 }];

        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Contabilidade");
        XLSX.writeFile(workbook, "Relatorio_Contabilidade.xlsx");
    };

    const totalValor = records.reduce((sum, r) => sum + (Number(r.valor) || 0), 0);

    return (
        <Template>
            <div className="h-auto w-full p-4 md:p-8 flex flex-col gap-6">
                <div className="text-stone-800 text-xl md:text-2xl font-medium">Relatório de Contabilidade</div>

                {(empresa || cnpj) && (
                    <div className="text-sm text-stone-600">
                        {empresa && <div><b>Empresa:</b> {empresa}</div>}
                        {cnpj && <div><b>CNPJ:</b> {cnpj}</div>}
                    </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
                    <div className="flex flex-col gap-1.5">
                        <label className="text-slate-700 text-sm font-medium">Data Inicial (Emissão)</label>
                        <input
                            type="date"
                            className="w-full px-3 py-2 bg-white rounded-md border border-slate-300"
                            value={filters.dataInicio}
                            onChange={(e) => setFilters({ ...filters, dataInicio: e.target.value })}
                        />
                    </div>
                    <div className="flex flex-col gap-1.5">
                        <label className="text-slate-700 text-sm font-medium">Data Final (Emissão)</label>
                        <input
                            type="date"
                            className="w-full px-3 py-2 bg-white rounded-md border border-slate-300"
                            value={filters.dataFim}
                            onChange={(e) => setFilters({ ...filters, dataFim: e.target.value })}
                        />
                    </div>
                    <div className="flex gap-2">
                        <button
                            className="px-4 py-2 w-full bg-white rounded-md border border-stone-500 flex items-center justify-center gap-2"
                            onClick={clearFilters}
                        >
                            <span className="text-stone-500 text-sm">Limpar</span>
                        </button>
                        <button
                            className="px-4 py-2 w-full bg-stone-600 rounded-md flex items-center justify-center gap-2"
                            onClick={applyFilters}
                        >
                            <span className="text-white text-sm">Filtrar</span>
                        </button>
                    </div>
                    <div className="flex justify-end">
                        <button
                            type="button"
                            className="px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-md flex items-center gap-2"
                            onClick={exportarExcel}
                        >
                            <img className="w-4 h-4" src="/images/icon/upload.png" alt="Excel" />
                            <span>Exportar Excel</span>
                        </button>
                    </div>
                </div>

                {isLoading && (
                    <div className="flex justify-center items-center py-8">
                        <div className="text-stone-600 text-sm">Carregando...</div>
                    </div>
                )}

                <div className="overflow-x-auto">
                    <table className="min-w-full bg-white border-collapse border border-gray-200">
                        <thead className="bg-stone-200">
                            <tr>
                                <th className="px-4 py-2 border border-slate-200 text-slate-800 text-sm font-bold">Data do Pagamento</th>
                                <th className="px-4 py-2 border border-slate-200 text-slate-800 text-sm font-bold">Número</th>
                                <th className="px-4 py-2 border border-slate-200 text-slate-800 text-sm font-bold">Razão Social</th>
                                <th className="px-4 py-2 border border-slate-200 text-slate-800 text-sm font-bold">Descrição</th>
                                <th className="px-4 py-2 border border-slate-200 text-slate-800 text-sm font-bold text-right">Valor</th>
                            </tr>
                        </thead>
                        <tbody>
                            {!isLoading && records.length > 0 ? (
                                records.map((r, idx) => (
                                    <tr key={idx} className="hover:bg-gray-50">
                                        <td className="px-4 py-2 border border-slate-200 text-slate-700 text-sm">{r.dataEmissao ? formatDateWithoutHour(r.dataEmissao) : ""}</td>
                                        <td className="px-4 py-2 border border-slate-200 text-slate-700 text-sm">{r.numero}</td>
                                        <td className="px-4 py-2 border border-slate-200 text-slate-700 text-sm">{(r.razaoSocial || "").toUpperCase()}</td>
                                        <td className="px-4 py-2 border border-slate-200 text-slate-700 text-sm">{r.tipoDespesa}</td>
                                        <td className="px-4 py-2 border border-slate-200 text-slate-700 text-sm text-right">{formatCurrencyFromDb(r.valor)}</td>
                                    </tr>
                                ))
                            ) : !isLoading ? (
                                <tr>
                                    <td colSpan={5} className="text-center py-10 text-slate-500">
                                        {hasSearched
                                            ? "Nenhum registro encontrado para o período."
                                            : "Defina o período e clique em Filtrar para carregar os dados."}
                                    </td>
                                </tr>
                            ) : null}
                        </tbody>
                        {records.length > 0 && (
                            <tfoot>
                                <tr className="bg-stone-100 font-semibold">
                                    <td className="px-4 py-2 border border-slate-200 text-right" colSpan={4}>Total</td>
                                    <td className="px-4 py-2 border border-slate-200 text-right">{formatCurrencyFromDb(totalValor)}</td>
                                </tr>
                            </tfoot>
                        )}
                    </table>
                </div>

                <AlertDialog isVisible={showSuccessDialog} message={message} onClose={() => setShowSuccessDialog(false)} />
            </div>
        </Template>
    );
};

export default RelatorioContabilidade;
